import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem("ct_chat_session");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [starting, setStarting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || !session) return;
    const load = () =>
      api
        .get(`/chat/${session.session_id}/messages`)
        .then((r) => setMessages(r.data))
        .catch((err) => {
          if (err.response?.status === 404) {
            localStorage.removeItem("ct_chat_session");
            setSession(null);
            setMessages([]);
          }
        });
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open, session]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, open]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/chat/start", {
        name: name.trim(),
        email: email.trim() || null,
      });
      localStorage.setItem("ct_chat_session", JSON.stringify(data));
      setSession(data);
    } catch {
      toast.error("Could not start chat. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      const { data } = await api.post(`/chat/${session.session_id}/messages`, { text: t });
      setMessages((m) => [...m, data]);
    } catch {
      toast.error("Message failed to send");
      setText(t);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with us"
        data-testid="chat-widget-toggle"
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-full shadow-2xl px-5 h-14 transition-transform hover:scale-105"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        <span className="font-medium text-sm hidden sm:inline">{open ? "Close" : "Chat with us"}</span>
      </button>

      {open && (
        <div
          className="fixed bottom-24 left-6 z-50 w-[calc(100vw-3rem)] max-w-sm bg-white rounded-2xl shadow-2xl border border-primary/10 flex flex-col overflow-hidden"
          style={{ height: "28rem" }}
          data-testid="chat-widget-panel"
        >
          <div className="bg-primary text-white px-5 py-4">
            <h3 className="font-serif font-bold">CITIZEN TRAVELS</h3>
            <p className="text-xs text-white/80">We usually reply within a few hours</p>
          </div>

          {!session ? (
            <form onSubmit={handleStart} className="flex-1 p-5 flex flex-col gap-4 justify-center">
              <p className="text-sm text-muted-foreground">
                Start a private chat with our team. Tell us who you are:
              </p>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name *"
                data-testid="chat-name-input"
                className="bg-white"
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional)"
                data-testid="chat-email-input"
                className="bg-white"
              />
              <Button
                type="submit"
                disabled={starting}
                data-testid="chat-start-btn"
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Chat"}
              </Button>
            </form>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-primary-foreground/40" data-testid="chat-messages">
                {messages.length === 0 && (
                  <p className="text-center text-xs text-muted-foreground pt-8">
                    Hi {session.name}! Send us a message and we'll get back to you.
                  </p>
                )}
                {messages.map((m) => (
                  <div
                    key={m.message_id}
                    className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm whitespace-pre-line ${
                        m.sender === "user"
                          ? "bg-primary text-white rounded-br-sm"
                          : "bg-white border border-primary/10 text-foreground rounded-bl-sm"
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={handleSend} className="p-3 border-t border-primary/10 flex gap-2 bg-white">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  data-testid="chat-message-input"
                  className="bg-white flex-1"
                />
                <Button
                  type="submit"
                  size="icon"
                  data-testid="chat-send-btn"
                  className="bg-primary text-white hover:bg-primary/90 rounded-full shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
